import { CalendarClock } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { Banner, Button, Checkbox, Field, Group, Input, Pill, Row, Select } from '@/components/ui/core';
import { api } from '@/lib/api';
import { relative } from '@/lib/format';
import { useAction, useApi } from '@/lib/hooks';
import type { Connection } from '@/lib/types';

interface Item {
  priority: 1 | 2 | 3;
  text: string;
  at: string | null;
  project: { slug: string; name: string } | null;
  link: string;
}
interface Digest {
  generatedAt: string;
  facts: Item[];
  unknowns: Item[];
  counts: { critical: number; attention: number; info: number };
}

const TONE = { 1: 'bad', 2: 'warn', 3: 'neutral' } as const;
const LABEL = { 1: 'Critique', 2: 'À surveiller', 3: 'Info' } as const;

function ItemRow({ i }: { i: Item }) {
  return (
    <Row label={<Link to={i.link} className="flex flex-wrap items-center gap-2 hover:underline"><Pill tone={TONE[i.priority]}>{LABEL[i.priority]}</Pill>{i.project && <span className="font-medium">{i.project.name}</span>}<span>{i.text}</span></Link>}>
      <span className="text-caption text-muted">{i.at ? relative(i.at) : 'non daté'}</span>
    </Row>
  );
}

/** F14 — prioritised summary built only from dated facts; unknowns listed apart. */
export function DigestCard() {
  const q = useApi<Digest>('/digest', { refetchInterval: 5 * 60_000, retry: false });
  const [all, setAll] = useState(false);
  if (!q.data) return null;
  const d = q.data;
  const facts = all ? d.facts : d.facts.filter((f) => f.priority < 3).slice(0, 8);
  return (
    <Group
      title="Résumé du jour"
      description={`Faits datés relevés par la console, du plus urgent au moins urgent. Les informations que la console ne peut pas connaître sont listées à part. Établi ${relative(d.generatedAt)}.`}
      actions={<Button size="sm" variant="ghost" onClick={() => setAll(!all)}>{all ? 'Réduire' : 'Tout afficher'}</Button>}
    >
      {facts.length === 0 ? <Row label={<span className="text-muted">Aucun fait critique ou à surveiller sur 24 h.</span>}>{null}</Row> : facts.map((i, n) => <ItemRow key={n} i={i} />)}
      {d.unknowns.length > 0 && (
        <>
          <div className="border-t border-hairline bg-surface-2 px-4 py-2 text-caption font-medium">Informations indisponibles ({d.unknowns.length})</div>
          {(all ? d.unknowns : d.unknowns.slice(0, 4)).map((i, n) => <ItemRow key={`u${n}`} i={i} />)}
        </>
      )}
    </Group>
  );
}

interface Settings {
  enabled: boolean;
  hour: number;
  timezone: string;
  connectionIds: string[];
  email: boolean;
}

export function DigestSettingsGroup({ connections }: { connections: Connection[] }) {
  const q = useApi<Settings>('/settings/digest');
  const [form, setForm] = useState<Settings | null>(null);
  const s = form ?? q.data;
  const save = useAction(() => api.put('/settings/digest', s), { success: 'Résumé quotidien enregistré', invalidate: ['/settings/digest'], onSuccess: () => setForm(null) });
  const send = useAction(() => api.post<{ sent: boolean; channels: number; errors: string[] }>('/digest/send'), { success: (r) => `Résumé envoyé${r.channels ? ` sur ${r.channels} canal(aux)` : ''}${r.errors.length ? ` — ${r.errors.length} erreur(s)` : ''}` });
  if (!s) return null;
  const set = (patch: Partial<Settings>) => setForm({ ...s, ...patch });
  const channels = connections.filter((c) => !c.revokedAt && c.capabilities.includes('notify'));
  return (
    <Group
      title="Résumé quotidien"
      description="Envoyé chaque jour aux propriétaires et administrateurs (dans la console) et sur les canaux choisis. Il ne contient que des faits datés et la liste de ce qui n’est pas connu."
      actions={<span className="flex gap-2"><Button size="sm" icon={<CalendarClock className="size-3.5" />} loading={send.isPending} onClick={() => send.mutate(undefined)}>Envoyer maintenant</Button><Button size="sm" variant="primary" disabled={!form} loading={save.isPending} onClick={() => save.mutate(undefined)}>Enregistrer</Button></span>}
    >
      <div className="space-y-3 p-4">
        {save.error && <Banner tone="critical">{save.error.message}</Banner>}
        <Checkbox checked={s.enabled} onChange={(e) => set({ enabled: e.target.checked })} label="Envoyer le résumé chaque jour" />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Heure">
            <Select value={String(s.hour)} onChange={(e) => set({ hour: Number(e.target.value) })}>
              {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, '0')} h</option>)}
            </Select>
          </Field>
          <Field label="Fuseau horaire"><Input value={s.timezone} onChange={(e) => set({ timezone: e.target.value })} /></Field>
        </div>
        <Field label="Canaux" hint={channels.length ? undefined : 'Connectez Slack, Discord, SMTP ou un webhook dans Connecteurs.'}>
          <div className="flex flex-wrap gap-3">
            {channels.map((c) => <Checkbox key={c.id} label={c.name} checked={s.connectionIds.includes(c.id)} onChange={(e) => set({ connectionIds: e.target.checked ? [...s.connectionIds, c.id] : s.connectionIds.filter((x) => x !== c.id) })} />)}
          </div>
        </Field>
      </div>
    </Group>
  );
}
